import type { RealtimeChannel } from '@supabase/supabase-js';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react';
import { supabase } from './supabase';

/**
 * Realtime hints (ADR-020, owner refinement 4). Database triggers publish tiny messages on private
 * per-location topics; a hint only invalidates queries. The queries stay the source of truth, and
 * TanStack Query's refetch on focus, on reconnect and on an interval remain the safety net.
 */
export type ChannelState = 'idle' | 'connecting' | 'live' | 'error';

export interface Hint {
  kind: string;
  id?: string;
  order_id?: string;
  station_id?: string;
  status?: string;
  version?: number;
  op?: string;
}

export const topicPos = (locationId: string) => `loc:${locationId}:pos`;
export const topicStation = (locationId: string, stationId: string) =>
  `loc:${locationId}:station:${stationId}`;

interface Sub {
  topic: string;
  channel: RealtimeChannel;
  listeners: Set<(h: Hint) => void>;
  state: ChannelState;
  /** Last join error, for the header pill's tooltip and the runbook. */
  error: string | null;
}
const subs = new Map<string, Sub>();
const stateListeners = new Map<string, Set<() => void>>();

function notify(topic: string) {
  stateListeners.get(topic)?.forEach((l) => l());
}

function setState(s: Sub, state: ChannelState, error: string | null = null) {
  if (s.state === state && s.error === error) return;
  s.state = state;
  s.error = error;
  notify(s.topic);
}

function ensure(topic: string): Sub {
  const existing = subs.get(topic);
  if (existing) return existing;
  const channel = supabase.channel(topic, { config: { private: true } });
  const s: Sub = { topic, channel, listeners: new Set(), state: 'connecting', error: null };
  subs.set(topic, s);
  channel.on('broadcast', { event: 'hint' }, (msg) => {
    const payload = (msg.payload ?? {}) as Hint;
    s.listeners.forEach((l) => l(payload));
  });
  // Private channels are authorised with the caller's JWT (row security on realtime.messages).
  void supabase.auth.getSession().then(({ data }) => {
    if (subs.get(topic) !== s) return; // everyone left before the session resolved
    if (data.session) void supabase.realtime.setAuth(data.session.access_token);
    channel.subscribe((status, err) => {
      if (status === 'SUBSCRIBED') {
        setState(s, 'live');
        // Anything that happened while we were away: refetch on (re)subscribe.
        s.listeners.forEach((l) => l({ kind: 'resubscribed' }));
      } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
        const detail = err ? String(err instanceof Error ? err.message : err) : status;
        if (detail !== s.error) console.warn(`[realtime] ${topic}: ${detail}`);
        setState(s, 'error', detail);
      } else if (status === 'CLOSED') setState(s, 'idle');
      else setState(s, 'connecting');
    });
  });
  notify(topic);
  return s;
}

/** Listen to hints on a topic. The channel closes when its last listener leaves. */
export function subscribeHints(topic: string, onHint: (h: Hint) => void): () => void {
  const s = ensure(topic);
  s.listeners.add(onHint);
  return () => {
    s.listeners.delete(onHint);
    if (s.listeners.size === 0 && subs.get(topic) === s) {
      subs.delete(topic);
      void supabase.removeChannel(s.channel);
      notify(topic);
    }
  };
}

/** Invalidate the given query keys on every hint of a topic (and on resubscribe). */
export function useRealtimeInvalidate(
  topic: string | null,
  keys: readonly (readonly string[])[],
  onHint?: (h: Hint) => void,
) {
  const qc = useQueryClient();
  const keyStr = JSON.stringify(keys);
  const onHintRef = useRef(onHint);
  useEffect(() => {
    onHintRef.current = onHint;
  }, [onHint]);
  useEffect(() => {
    if (!topic) return;
    const parsed = JSON.parse(keyStr) as string[][];
    return subscribeHints(topic, (h) => {
      for (const k of parsed) void qc.invalidateQueries({ queryKey: k });
      onHintRef.current?.(h);
    });
  }, [topic, qc, keyStr]);
}

export interface ChannelStatus {
  state: ChannelState;
  error: string | null;
}
const IDLE: ChannelStatus = { state: 'idle', error: null };
const statusCache = new Map<string, ChannelStatus>();
function statusOf(topic: string): ChannelStatus {
  const s = subs.get(topic);
  if (!s) return IDLE;
  const cached = statusCache.get(topic);
  if (cached && cached.state === s.state && cached.error === s.error) return cached;
  const next = { state: s.state, error: s.error };
  statusCache.set(topic, next);
  return next;
}

/** Connection state of a topic for the header pill; `idle` when no subscription is open. */
export function useChannelState(topic: string | null): ChannelStatus {
  const subscribe = useCallback(
    (l: () => void) => {
      if (!topic) return () => undefined;
      let set = stateListeners.get(topic);
      if (!set) {
        set = new Set();
        stateListeners.set(topic, set);
      }
      set.add(l);
      return () => {
        set.delete(l);
      };
    },
    [topic],
  );
  const get = useCallback(() => (topic ? statusOf(topic) : IDLE), [topic]);
  return useSyncExternalStore(subscribe, get, get);
}
